import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import 'react-datepicker/dist/react-datepicker.css';
import '../styles/DatePicker.css';
import '../styles/ReactTags.css';
import { useTrendsData, useProcessedTrendData, getPresetDateRange } from '../hooks/useTrendsData';
import { getDefaultAggregation } from '../config/trendsConfig';
import { getDefaultChartType } from '../config/chartTypeConfig';
import PeriodControls from './trends/PeriodControls';
import TrendsChart from './trends/TrendsChart';
import TrendsSummaryWidgets from './trends/TrendsSummaryWidgets';
import TrendsDataTable from './trends/TrendsDataTable';
import useTrendsTour from './trends/useTrendsTour';
import ErrorMessage from './common/ErrorMessage';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const Trends = ({ hasSeenNavbarTour }) => {
  const { isDark } = useTheme();
  const { isAuthenticated, userInfo } = useAuth();
  const isAdmin = userInfo?.role === 'admin';
  const [selectedPeriod, setSelectedPeriod] = useState('365D');
  const [previousPeriod, setPreviousPeriod] = useState('365D');
  const [manualAggregation, setManualAggregation] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({
    fromDate: null,
    toDate: null
  });
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [showBreakdown, setShowBreakdown] = useState(true);
  const [chartType, setChartType] = useState(getDefaultChartType());
  const [areaChartStacked, setAreaChartStacked] = useState(true);

  const { trendData, countries, profiles, isLoading, error, fetchTrendsData } = useTrendsData();
  
  // Custom aggregation logic that preserves aggregation when switching to Custom
  const getCurrentAggregation = () => {
    if (manualAggregation) {
      return manualAggregation;
    }
    if (selectedPeriod === 'CUSTOM') {
      return getDefaultAggregation(previousPeriod);
    }
    return getDefaultAggregation(selectedPeriod);
  };
  
  const currentAggregation = getCurrentAggregation();
  const [displayTrendData, setDisplayTrendData] = useState([]);
  const processedTrendData = useProcessedTrendData(trendData, currentAggregation);

  // Calculate current date range for display
  const getCurrentDateRange = () => {
    const useCustomRange = customDateRange.fromDate && customDateRange.toDate;
    if (useCustomRange) {
      return { fromDate: customDateRange.fromDate, toDate: customDateRange.toDate };
    } else {
      return getPresetDateRange(selectedPeriod);
    }
  };

  const currentDateRange = getCurrentDateRange();

  // Update display data when not loading - clear when empty, update when has data
  useEffect(() => {
    if (!isLoading) {
      setDisplayTrendData(processedTrendData);
    }
  }, [processedTrendData, isLoading]);

  // Use the extracted tour hook
  const { startTour } = useTrendsTour({ isAuthenticated, processedTrendData: displayTrendData, countries, hasSeenNavbarTour });

  useEffect(() => {
    if (!isAuthenticated) return;

    const useCustomRange = customDateRange.fromDate && customDateRange.toDate;
    let dateRange;
    
    if (useCustomRange) {
      dateRange = { fromDate: customDateRange.fromDate, toDate: customDateRange.toDate };
    } else {
      dateRange = getPresetDateRange(selectedPeriod);
    }

    fetchTrendsData(dateRange.fromDate, dateRange.toDate, selectedCountries, selectedClient);
  }, [selectedPeriod, customDateRange, selectedCountries, selectedClient, isAuthenticated, fetchTrendsData]);

  return (
    <div className="bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Trends
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Diet Request Trends Over Time
            </p>
          </div>

          <button
            onClick={startTour}
            className="hidden md:flex flex-col items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
          >
            <svg
              className="w-5 h-5 mb-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            <span className="text-xs font-medium">Tour</span>
          </button>
        </div>

        {error && (
          <ErrorMessage
            title="Error loading trends data"
            message={error}
          />
        )}

        <PeriodControls
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          previousPeriod={previousPeriod}
          setPreviousPeriod={setPreviousPeriod}
          customDateRange={customDateRange}
          setCustomDateRange={setCustomDateRange}
          selectedCountries={selectedCountries}
          setSelectedCountries={setSelectedCountries}
          countries={countries}
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          profiles={profiles}
          isAdmin={isAdmin}
          manualAggregation={manualAggregation}
          setManualAggregation={setManualAggregation}
          currentAggregation={currentAggregation}
          currentDateRange={currentDateRange}
        />

        <div className="relative">
          <TrendsChart
            processedTrendData={displayTrendData}
            showBreakdown={showBreakdown}
            setShowBreakdown={setShowBreakdown}
            isDark={isDark}
            selectedPeriod={selectedPeriod}
            currentAggregation={currentAggregation}
            chartType={chartType}
            setChartType={setChartType}
            areaChartStacked={areaChartStacked}
            setAreaChartStacked={setAreaChartStacked}
          />

          <TrendsSummaryWidgets
            processedTrendData={displayTrendData}
            currentAggregation={currentAggregation}
          />

          <TrendsDataTable
            processedTrendData={displayTrendData}
            selectedPeriod={selectedPeriod}
            currentAggregation={currentAggregation}
          />

          {/* Loading Overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Trends;